import { useState } from "react";
import { Building2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConnections } from "@/hooks/useConnections";
import type { Connection } from "@/services/connectionService";
import { statusBadgeVariant, EmptyState } from "./helpers";


export function ConnectionsList({ onOpen }: { onOpen: (id: string) => void }) {
  const [filterStatus, setFilterStatus] = useState<string | undefined>(
    undefined,
  );
  const { data: connections, isLoading } = useConnections(filterStatus);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {[undefined, "active", "pending", "suspended", "closed"].map((s) => (
          <Button
            key={s ?? "all"}
            variant={filterStatus === s ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterStatus(s)}
          >
            {s ?? "All"}
          </Button>
        ))}
      </div>

      {!connections || connections.length === 0 ? (
        <EmptyState
          icon={<Building2 className="h-10 w-10 text-muted-foreground/60" />}
          title="No connections yet"
          description="Connect with a vendor or distributor to start exchanging stock."
        />
      ) : (
        <div className="space-y-3">
          {connections.map((conn) => (
            <ConnectionRow key={conn._id} connection={conn} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectionRow({
  connection,
  onOpen,
}: {
  connection: Connection;
  onOpen: (id: string) => void;
}) {
  const otherSide =
    connection.myRole === "distributor"
      ? connection.vendor
      : connection.distributor;

  return (
    <Card
      className="p-4 hover:bg-accent/30 cursor-pointer transition-colors"
      onClick={() => onOpen(connection._id)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-medium truncate">{otherSide.displayName}</h3>
            <Badge variant={statusBadgeVariant(connection.status)}>
              {connection.status}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {connection.vendorOnEaze ? "On Eaze" : "External"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            You are <strong className="font-medium">{connection.myRole}</strong>
            {connection.autoPostOnClose && (
              <>
                {" · "}
                <span className="text-xs font-medium text-primary">
                  Auto-post journals on close
                </span>
              </>
            )}
          </p>
        </div>
        <div className="text-xs text-muted-foreground text-right shrink-0">
          {formatDistanceToNow(new Date(connection.createdAt), {
            addSuffix: true,
          })}
        </div>
      </div>
    </Card>
  );
}

